import * as React from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import styled from 'styled-components';
import { Flex } from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import { DivMinWidth, FlexPadStyled } from '../App';
import { LogoBC } from '../components/LogoBC';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { MessageType } from '../../../common/types';
import { API } from '../common/constsAPI';
import { paths } from '../consts';
import { colors, fonts } from '../assets/constStyles';
import { extractTextFromAssistantContent } from '../functions';

interface ButtonProps{
  $loading: boolean;
}

const FlexChatStyled = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  padding-bottom: 20px;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${fonts.font2};


  && * {
    font-family: ${fonts.font2} !important;
  }
`;

export const ButtonStyled = styled.button<ButtonProps>`
  padding: 10px 18px;
  font-size: 16px;
  border-radius: 6px;
  border: none;
  background-color: ${({ $loading }) => ($loading ? colors.colorMutedYellow : colors.colorRed)};
  color: #fff;
  cursor: pointer;
  font-family: ${fonts.font2};
  
  &:hover {
    border-color: ${colors.colorMutedYellow} !important;
    background-color: ${({ $loading }) => ($loading ? colors.colorMutedYellow : colors.colorMutedYellow)} !important;
    transition: all 0.3s ease;
  }

  @media (max-width: 768px) {
    padding: 10px 20px!important;
    font-size: 14px!important;
  }
`;

const SpanStyled = styled.span`
  white-space: pre-wrap;
`;

const DivChatContainerStyled = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  min-height: 300px;
`;

const CustomTextArea = styled.textarea`
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 10px;
  font-size: 16px;
  width: 100%;

  &:focus {
    border: 1px solid ${colors.colorTextDark};
    box-shadow: none;
    outline: none;
  }
`;

export const ChatPage: React.FC = () => {
  const [threadId, setThreadId] = useState<string>('');
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage: MessageType = { sender: 'user', text: inputText };
    setMessages((prev) => [...prev, userMessage]);

    const typingMessage: MessageType = { sender: 'assistant', text: 'Typing...' };
    setMessages((prev) => [...prev, typingMessage]);
    setInputText('');
    setLoading(true);

    try {
      const response = await axios.post(`${API.URL_REST_API}${paths.CHAT}`, {
        text: inputText,
        idThread: threadId,
      });
      console.log(response);

      const { content, idThread: returnedThreadId } = response.data;

      if (returnedThreadId && returnedThreadId !== threadId) {
        setThreadId(returnedThreadId); // сохранить threadId
      }

      const assistantReply = extractTextFromAssistantContent(content);

      setMessages((prev) => [
        ...prev.slice(0, -1),
        { sender: 'assistant', text: assistantReply || '[No response]' },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { sender: 'assistant', text: '⚠️ Error: Unable to contact assistant.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const buttonDisabled = !(loading || inputText.trim());

  return <>
        <Helmet>
            <title>CHAT</title>
            <meta
                name="description"
                content="AWMAC - Architectural Woodwork Manufacturers Association of Canada AWARDS"
            />
            <link rel="canonical" href='https://awmacbc.com/chat'/>
        </Helmet>
        <div style={{ position: 'relative' }}>
            <DivMinWidth>
                <FlexPadStyled vertical>
                    <LogoBC />
                    <Breadcrumbs />
                </FlexPadStyled>
                <FlexChatStyled>
                    <h2>🤖 Chat with Assistant</h2>
                    <span>📄 NAAWS - 4.0 Current Edition</span>
                    <DivChatContainerStyled>
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                style={{
                                  textAlign: msg.sender === 'user' ? 'right' : 'left',
                                  marginBottom: '8px',
                                }}
                            >
                                <div
                                    style={{
                                      display: 'inline-block',
                                      background: msg.sender === 'user' ? '#e6f7ff' : '#f0f0f0',
                                      padding: '8px 12px',
                                      borderRadius: '8px',
                                      maxWidth: '90%',
                                    }}
                                >
                                <strong>{msg.sender === 'user' ? '🧑 You' : '🤖 AI'}:</strong>{' '}
                                    <SpanStyled>
                                        {msg.text}
                                    </SpanStyled>
                                </div>
                            </div>
                        ))}
                    </DivChatContainerStyled>
                    <p style={{ fontWeight: 700, fontSize: '10px' }}>IMPORTANT: Page numbers may differ from those in the original PDF!</p>
                    <Flex gap={10} style={{ marginTop: 10 }}>
                        <CustomTextArea
                            value={inputText}
                            disabled={loading}
                            onChange={(e:any) => setInputText(e.target.value)}
                            placeholder="Ask a question about the document..."
                            maxLength={120}
                        />
                        <ButtonStyled $loading={buttonDisabled}
                                      disabled={buttonDisabled}
                                      onClick={sendMessage}>{loading ? '...' : <ArrowUpOutlined/>}
                        </ButtonStyled>
                    </Flex>
                </FlexChatStyled>
            </DivMinWidth>
        </div>
    </>;
};
